import { Dispatch, ReactElement, ReactNode, SetStateAction, useEffect, useState, ButtonHTMLAttributes } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LootBoxInstanceWithConfig, OpenLootBoxInstanceResponse, RewardType } from "@devour/client";
import classNames from "classnames";
import { IStore } from "@/redux/defaultStore";
import { addError } from "@/redux/meta/metaActions";
import { useOpenLootBox } from "@/hooks/useOpenLootBox";
import useRefreshRewardData from "@/hooks/useRefreshRewardData";
import {Button} from "@/components/buttons/Button";
import LootCrate from "@/components/lootbox/LootCrate";

interface Props {
    item: LootBoxInstanceWithConfig;
    toggleDrawer: () => void;
    setSelectedVirtualInstance: Dispatch<SetStateAction<LootBoxInstanceWithConfig>>;
}

export enum VirtualLootBoxRedemptionState {
    NOT_INITIATED,
    IN_PROGRESS,
    DONE,
}

export default function VirtualLootBoxSelected(props: Props): ReactElement {
    const dispatch = useDispatch();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const { item } = props;
    const config = item.config;

    const [redemptionState, setRedemptionState] = useState<VirtualLootBoxRedemptionState>(VirtualLootBoxRedemptionState.NOT_INITIATED);
    const [revealPrize, setRevealPrize] = useState<boolean>(false);
    const [prizeResult, setPrizeResult] = useState<OpenLootBoxInstanceResponse>(undefined);

    const { mutateAsync: openLootBox } = useOpenLootBox(fullToken);
    const { refetch: refreshRewardData } = useRefreshRewardData();

    const sparklingEffectTimeout: number = config?.transitionEffectEnabled ? 1500 : 0;
    const boxImage = config?.image ?? `${import.meta.env.VITE_CDN_URL}/images/govip-nft-rewards-default-image.gif`;
    const animationImage = config?.redemptionAnimation ?? boxImage;

    useEffect(() => {
        setRedemptionState(VirtualLootBoxRedemptionState.NOT_INITIATED);
        setRevealPrize(false);
        setPrizeResult(undefined);
    }, [item.instance.id]);

    async function onOpen(): Promise<void> {
        setRedemptionState(VirtualLootBoxRedemptionState.IN_PROGRESS);
        try {
            const result = await openLootBox(item.instance.id);
            setRevealPrize(true);
            setTimeout(() => {
                setPrizeResult(result);
                setRedemptionState(VirtualLootBoxRedemptionState.DONE);
            }, sparklingEffectTimeout);
            // Refresh XP balance — every LootBox prize type can award XP
            await refreshRewardData(RewardType.XP);
        } catch (e) {
            setRedemptionState(VirtualLootBoxRedemptionState.NOT_INITIATED);
            dispatch(await addError(e));
        }
    }

    function getDisplayImage(): string {
        if (redemptionState === VirtualLootBoxRedemptionState.NOT_INITIATED || !config?.redemptionAnimation) {
            return boxImage;
        }
        return animationImage;
    }

    function renderPrizeInfo(): ReactElement {
        const today = new Date();
        const month = today.toLocaleString("en-us", { month: "short" });
        const day = today.getDate();
        const year = today.getFullYear();

        const prizeName = prizeResult.prizeName;
        const promoCode = prizeResult.promoCode;
        const promoName = prizeResult.promoName;

        const extraInfo: ReactNode = promoCode
            ? <> Code <strong>{promoCode}</strong> for <strong>{promoName}</strong> has been added to your account. Apply it at checkout and enjoy!</>
            : null;

        return (
            <div className="govip-rewards-drawer_reward_info">
                <div className="govip-rewards-drawer_reward_info_origin">
                    <span>Prize</span>
                </div>
                <div className="govip-rewards-drawer_reward_info_title">{prizeName}</div>
                <div className="govip-rewards-drawer_reward_info_nftId">Redeemed on {month} {day}, {year}</div>
                <div className="govip-rewards-drawer_reward_info_description">
                    You have successfully opened <strong>{config?.name ?? "Loot Box"}</strong> and claimed <strong>{prizeName}</strong>.
                    {extraInfo}
                </div>
            </div>
        );
    }

    function renderFooter(): ReactElement {
        if (redemptionState === VirtualLootBoxRedemptionState.IN_PROGRESS) {
            return (
                <div className="govip-rewards-drawer_footer_in-progress">
                    Unlocking
                    <div className="loading-dot" />
                    <div className="loading-dot" />
                    <div className="loading-dot" />
                </div>
            );
        }
        if (redemptionState === VirtualLootBoxRedemptionState.DONE) {
            return (
                <div className="govip-rewards-drawer_footer_default">
                    <Button variant="ghost"
                        className="govip-rewards-drawer_footer_default_button"
                        onClick={props.toggleDrawer}
                    >
                        Confirm and close
                    </Button>
                </div>
            );
        }
        return (
            <div className="govip-rewards-drawer_footer_default">
                <Button variant="ghost"
                    className="govip-rewards-drawer_footer_default_button"
                    onClick={onOpen}
                >
                    Open Now
                </Button>
            </div>
        );
    }

    return (
        <>
            <div className="govip-rewards-drawer_reward">
                <div className="govip-rewards-drawer_reward_top">
                    <div
                        className={classNames("govip-rewards-drawer_reward_top_image-container", "winning-image", {
                            show: !!prizeResult,
                        })}
                    >
                        {prizeResult &&
                            <img
                                src={boxImage}
                                alt="Prize Won"
                                className="prize-img"
                            />
                        }
                    </div>

                    <div
                        className={classNames("govip-rewards-drawer_reward_top_image-container", "reward-image", {
                            show: !prizeResult,
                        })}
                    >
                        {config?.crateColor?.g1 && config?.crateColor?.g2
                            ? <LootCrate
                                state={redemptionState === VirtualLootBoxRedemptionState.NOT_INITIATED ? "closed" : "play"}
                                g1={config.crateColor.g1}
                                g2={config.crateColor.g2}
                                size="100%"
                            />
                            : <img src={getDisplayImage()} alt="Loot Box" />
                        }

                        <img
                            src={`${import.meta.env.VITE_CDN_URL}/images/govip-rewards-light-effect.gif`}
                            alt="Light Effect"
                            className={classNames("light-effect", {
                                show: revealPrize && !prizeResult && config?.transitionEffectEnabled,
                            })}
                        />
                    </div>
                </div>

                {prizeResult
                    ? renderPrizeInfo()
                    : <div className="govip-rewards-drawer_reward_info">
                        <div className="govip-rewards-drawer_reward_info_origin">
                            <span>Virtual Loot Box</span>
                        </div>
                        <div className="govip-rewards-drawer_reward_info_title">{config?.name ?? "Loot Box"}</div>
                        <div className="govip-rewards-drawer_reward_info_description">
                            {config?.description ?? "Open this box to reveal your prize!"}
                        </div>
                    </div>

                }
            </div>

            <div className="govip-rewards-drawer_footer">
                {renderFooter()}
            </div>
        </>
    );
}
